<template>
  <div class="payroll-view">
    <v-container fluid class="pt-4">
      <h2 class="mb-2">Payroll</h2>
      <p class="caption grey--text text--darken-1 mb-4">
        Monthly 出勤簿 per employee — daily start/finish times derived from
        NekoHR timesheets (9:00 start, 12:00–13:00 break when ≥5h logged). Month
        list defaults to last month; the current month appears only in the last
        3 days of that month.
      </p>

      <v-row align="center" class="mb-3">
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="selectedMonthKey"
            :items="monthOptions"
            label="Month"
            outlined
            dense
            hide-details
            @change="onMonthChange"
          />
        </v-col>
        <v-col cols="12" sm="5" md="4">
          <v-select
            v-model="selectedEmployeeUserId"
            :items="employeeOptions"
            label="Employee"
            outlined
            dense
            hide-details
            :disabled="loading || !!loadError || !employeeOptions.length"
            @change="onEmployeeChange"
          />
        </v-col>
        <v-col cols="12" sm="3" md="2">
          <v-btn
            color="primary"
            block
            :loading="exporting"
            :disabled="exporting"
            @click="exportWorkbook"
          >
            <v-icon left small>mdi-download</v-icon>
            Export
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" md="3">
          <div v-if="loading" class="body-2">Loading timesheets…</div>
          <div v-else-if="loadError" class="error--text body-2">
            {{ loadError }}
          </div>
          <div v-else-if="selectedRosterRow" class="body-2">
            <strong>{{ displayMonthLabel }}</strong>
            — {{ selectedRosterRow.fullName || selectedRosterRow.name }}
            <span class="grey--text">({{ selectedRosterRow.empOutId }})</span>
          </div>
        </v-col>
      </v-row>

      <v-alert v-if="loadError" type="error" dense outlined class="mb-3">
        {{ loadError }}
      </v-alert>
      <v-alert v-if="exportError" type="error" dense outlined class="mb-3">
        {{ exportError }}
      </v-alert>
      <v-alert
        v-if="loadWarning && !loadError"
        type="warning"
        dense
        outlined
        class="mb-3"
      >
        {{ loadWarning }}
      </v-alert>
      <v-alert
        v-if="
          !loading &&
          !loadError &&
          selectedRosterRow &&
          !selectedRosterRow.matchedInNekohr
        "
        type="info"
        dense
        outlined
        class="mb-3"
      >
        No NekoHR match for this roster entry — timesheet data may be empty.
      </v-alert>
    </v-container>

    <v-container
      v-if="!loading && !loadError && selectedRosterRow && attendanceBook"
      fluid
      class="pt-0"
    >
      <v-alert
        v-if="selectedPayrollFlags.monthlyTreatment"
        type="info"
        dense
        outlined
        class="mx-3 mb-3"
      >
        Monthly employee (月給): distributing
        <strong>{{ selectedPayrollFlags.monthlyTargetHours }}</strong>
        hours across weekdays. PTO lines from NekoHR are kept; other timesheet
        hours are ignored.
      </v-alert>
      <div class="payroll-attendance-header mb-3 px-3">
        <div class="subtitle-1 font-weight-medium">
          出勤簿 <span class="payroll-bilingual-en">/ Attendance record</span>
          <span
            v-if="selectedPayrollFlags.monthlyTreatment"
            class="payroll-bilingual-en"
          >
            — 月給
          </span>
        </div>
        <div class="body-2">
          氏名 <span class="payroll-bilingual-en">/ Name</span>:
          <strong>{{ attendanceHeaderName }}</strong>
          <span class="ml-4 grey--text">{{ selectedRosterRow.empOutId }}</span>
        </div>
      </div>

      <div class="payroll-attendance-table-wrap">
        <v-simple-table dense class="payroll-attendance-table">
          <thead>
            <tr>
              <th v-for="col in attendanceColumns" :key="col.key">
                {{ col.label }}
                <span class="payroll-bilingual-en">/ {{ col.labelEn }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in attendanceBook.dailyRows"
              :key="row.dateKey"
              :class="{
                'payroll-day--weekend': row.isWeekend,
                'payroll-day--holiday': row.isHoliday && !row.workHoursDecimal,
                'payroll-day--worked': row.workHoursDecimal > 0,
              }"
            >
              <td>{{ row.dateLabel }}</td>
              <td>
                {{ row.weekday }}
                <span v-if="row.weekdayEn" class="payroll-bilingual-en">
                  / {{ row.weekdayEn }}
                </span>
              </td>
              <td>{{ row.startTime || "—" }}</td>
              <td>{{ row.finishTime || "—" }}</td>
              <td>{{ row.breakStart || "—" }}</td>
              <td>{{ row.breakEnd || "—" }}</td>
              <td class="font-weight-medium">{{ row.totalTime || "—" }}</td>
              <td>
                <template v-if="row.leaveLabel">
                  {{ row.leaveLabel }}
                  <span v-if="row.leaveLabelEn" class="payroll-bilingual-en">
                    / {{ row.leaveLabelEn }}
                  </span>
                </template>
                <template v-else>—</template>
              </td>
              <td>{{ row.leaveHoursLabel || "—" }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>

      <div class="payroll-attendance-summary mt-4 px-3">
        <v-simple-table dense class="payroll-summary-table">
          <tbody>
            <tr v-for="item in summaryDisplayRows" :key="item.key">
              <td class="payroll-summary-label">
                {{ item.label }}
                <span class="payroll-bilingual-en">/ {{ item.labelEn }}</span>
              </td>
              <td class="payroll-summary-value font-weight-medium">
                {{ item.value }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </div>
    </v-container>
  </div>
</template>

<script>
import axios from "@/services/axios.config";
import {fetchAllFilteredTimesheets} from "@/services/timesheetListApi";
import {GetEndOfMonthDate, GetStartOfMonthDate} from "@/Utilites/timerHelper";
import {
  buildTimesheetRosterLookup,
  djangoUserIdForTimesheetRow,
  normalizeTimesheetRowUserId,
  resolveTeamRowUserId,
} from "@/utils/timesheetRosterLookup";
import {buildPayrollEmpReportRows} from "@/utils/payrollRosterReport";
import {
  buildKonekoTeamRosterMetaByUsername,
} from "@/config/konekoAConfig";
import {
  PAYROLL_ATTENDANCE_COLUMNS,
  PAYROLL_ATTENDANCE_SUMMARY_ROWS,
  buildPayrollAttendanceBook,
} from "@/utils/payrollAttendanceBook";
import {exportPayrollWorkbook} from "@/utils/payrollWorkbookExport";
import {buildPayrollFlagsByUserId} from "@/utils/payrollMemberFlags";
import {enrichTeamRowsFromTimesheetUsers} from "@/utils/payrollTeamRosterEnrich";
import {memberIncludedInPayrollMonth, memberWasActiveInCalendarMonth} from "@/utils/memberEmploymentDates";
import {isRealTeamMemberRow} from "@/Utilites/teamRosterApi";
import {
  coercePayrollMonthKey,
  defaultPayrollMonthKey,
  isPayrollMonthSelectable,
  listPayrollSelectableMonthKeys,
} from "@/utils/payrollMonthSelection";

function monthTitle(monthKey) {
  const [y, mo] = String(monthKey).split("-").map(Number);
  if (!y || !mo) return monthKey;
  const d = new Date(y, mo - 1, 1);
  return d.toLocaleDateString("en-GB", {month: "long", year: "numeric"});
}

function parseRowHours(row) {
  for (const key of ["hours_with_timer", "working_hours", "hours"]) {
    const raw = row[key];
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function dedupeTimesheetRows(rows) {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    if (!row) return;
    const id = row.id;
    if (id != null && id !== "") {
      const key = `id:${id}`;
      if (seen.has(key)) return;
      seen.add(key);
    } else {
      const uid = row.user_id;
      const dk = String(row.log_date || "").slice(0, 10);
      const pid = row.project_id;
      const h = parseRowHours(row);
      const key = `anon:${uid}|${dk}|${pid}|${h}|${row.log_description || ""}`;
      if (seen.has(key)) return;
      seen.add(key);
    }
    out.push(row);
  });
  return out;
}

export default {
  name: "PayrollView",
  data() {
    return {
      selectedMonthKey: defaultPayrollMonthKey(),
      monthOptions: listPayrollSelectableMonthKeys(48).map((value) => ({
        value,
        text: monthTitle(value),
      })),
      selectedEmployeeUserId: null,
      loading: false,
      loadError: "",
      loadWarning: "",
      allItems: [],
      teamRows: [],
      apiTeamRows: [],
      rosterMetaByUsername: {},
      rosterLookup: {},
      projectNameById: {},
      empReportRows: [],
      payrollFlagsByUserId: {},
      attendanceColumns: PAYROLL_ATTENDANCE_COLUMNS,
      exporting: false,
      exportError: "",
    };
  },
  computed: {
    displayMonthLabel() {
      return monthTitle(this.selectedMonthKey);
    },
    employeeOptions() {
      return (this.empReportRows || [])
        .map((row) => {
          const flags =
            row.userId != null
              ? this.payrollFlagsByUserId[row.userId] || {}
              : {};
          const monthly = flags.monthlyTreatment ? " [月給]" : "";
          const teamRow =
            row.userId != null
              ? this.teamRows.find(
                  (t) => resolveTeamRowUserId(t) === row.userId
                )
              : null;
          const inactive =
            teamRow && teamRow.is_currently_working === false
              ? " (not working)"
              : "";
          const ended =
            teamRow &&
            teamRow.last_working_date &&
            !memberWasActiveInCalendarMonth(teamRow, this.selectedMonthKey)
              ? " (after stint)"
              : "";
          const unmatched = row.userId == null ? " (no NekoHR link)" : "";
          return {
            value: row.userId,
            text: `${row.name}${monthly}${inactive}${ended}${unmatched} — ${
              row.fullName || row.nameInKoneko || row.empOutId
            }`,
            disabled: row.userId == null,
          };
        })
        .filter((opt) => opt.value != null);
    },
    selectedRosterRow() {
      if (this.selectedEmployeeUserId == null) return null;
      const uid = Number(this.selectedEmployeeUserId);
      return (
        (this.empReportRows || []).find((row) => row.userId === uid) || null
      );
    },
    selectedUserItems() {
      if (this.selectedEmployeeUserId == null) return [];
      const uid = Number(this.selectedEmployeeUserId);
      return this.allItems.filter(
        (row) => normalizeTimesheetRowUserId(row) === uid
      );
    },
    selectedPayrollFlags() {
      if (this.selectedEmployeeUserId == null) return {};
      return this.payrollFlagsByUserId[this.selectedEmployeeUserId] || {};
    },
    attendanceBook() {
      if (!this.selectedRosterRow) return null;
      return buildPayrollAttendanceBook(
        this.selectedMonthKey,
        this.selectedUserItems,
        this.projectNameById,
        this.selectedPayrollFlags
      );
    },
    attendanceHeaderName() {
      const row = this.selectedRosterRow;
      if (!row) return "";
      return row.fullName || row.nameInKoneko || row.name;
    },
    summaryDisplayRows() {
      if (!this.attendanceBook) return [];
      const s = this.attendanceBook.summary;
      return PAYROLL_ATTENDANCE_SUMMARY_ROWS.map(({key, label, labelEn}) => ({
        key,
        label,
        labelEn,
        value: s[key] != null && s[key] !== "" ? s[key] : "0",
      }));
    },
  },
  mounted() {
    this.refreshMonthOptions();
    this.loadPayrollData();
  },
  methods: {
    onMonthChange() {
      this.exportError = "";
      if (!isPayrollMonthSelectable(this.selectedMonthKey)) {
        this.selectedMonthKey = defaultPayrollMonthKey();
      }
      this.loadPayrollData();
    },
    refreshMonthOptions() {
      this.monthOptions = listPayrollSelectableMonthKeys(48).map((value) => ({
        value,
        text: monthTitle(value),
      }));
      this.selectedMonthKey = coercePayrollMonthKey(this.selectedMonthKey);
    },
    onEmployeeChange() {
      /* computed attendance book refreshes */
    },
    async exportWorkbook() {
      this.exportError = "";
      this.exporting = true;
      try {
        if (this.loading) {
          await new Promise((resolve, reject) => {
            const started = Date.now();
            const tick = () => {
              if (!this.loading) {
                resolve();
                return;
              }
              if (Date.now() - started > 320000) {
                reject(new Error("Timed out waiting for payroll data."));
                return;
              }
              setTimeout(tick, 200);
            };
            tick();
          });
        } else if (!this.empReportRows.length) {
          await this.loadPayrollData();
        }
        if (this.loadError) {
          throw new Error(this.loadError);
        }
        await exportPayrollWorkbook({
          monthKey: this.selectedMonthKey,
          empReportRows: this.empReportRows,
          allItems: this.allItems,
          projectNameById: this.projectNameById,
          teamRows: this.teamRows,
        });
      } catch (e) {
        this.exportError =
          (e && e.message) || "Failed to export payroll workbook.";
      } finally {
        this.exporting = false;
      }
    },
    pickDefaultEmployee() {
      const rows = this.empReportRows || [];
      const withHours = rows.find(
        (r) => r.userId != null && r.hoursDecimal > 0
      );
      const first = rows.find((r) => r.userId != null);
      const pick = withHours || first;
      this.selectedEmployeeUserId = pick ? pick.userId : null;
    },
    async fetchTeamRoster() {
      const mk = encodeURIComponent(this.selectedMonthKey || "");
      const q = `?include_not_working=1&for_month=${mk}`;
      let rows = [];
      try {
        const {data} = await axios.get(`api/users/team-members/list/${q}`, {
          timeout: 25000,
        });
        if (Array.isArray(data)) rows = data;
      } catch {
        /* fall through */
      }
      if (!rows.length) {
        try {
          const {data} = await axios.get(`api/users/team-members/${q}`, {
            timeout: 25000,
          });
          if (Array.isArray(data)) rows = data;
          else if (data && Array.isArray(data.results)) rows = data.results;
        } catch {
          /* fall through */
        }
      }
      return rows;
    },
    async fetchProjects() {
      const {data} = await axios.get("api/projects/list/", {timeout: 15000});
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.results)) return data.results;
      return [];
    },
    normalizeTimesheetRows(timesheets) {
      return (timesheets || [])
        .map((row) => {
          const uid = djangoUserIdForTimesheetRow(this.rosterLookup, row);
          const hours = parseRowHours(row);
          if (!row || !row.log_date) return null;
          return {
            ...row,
            user_id: uid != null ? uid : normalizeTimesheetRowUserId(row),
            working_hours: hours,
            log_description: row.log_description || row.task_description || "",
          };
        })
        .filter(Boolean);
    },
    async backfillMissingUsers(timesheets, teamRows) {
      const have = new Set();
      this.normalizeTimesheetRows(timesheets).forEach((row) => {
        const uid = normalizeTimesheetRowUserId(row);
        if (uid != null) have.add(uid);
      });
      const tasks = [];
      (teamRows || []).forEach((row) => {
        const uid = resolveTeamRowUserId(row);
        if (uid == null || have.has(uid)) return;
        if (!isRealTeamMemberRow(row)) return;
        if (!memberIncludedInPayrollMonth(row, this.selectedMonthKey)) return;
        tasks.push(uid);
      });
      if (!tasks.length) return timesheets;
      const start = GetStartOfMonthDate(`${this.selectedMonthKey}-01`);
      const end = GetEndOfMonthDate(`${this.selectedMonthKey}-01`);
      const extra = [];
      const batchSize = 6;
      for (let i = 0; i < tasks.length; i += batchSize) {
        const batch = tasks.slice(i, i + batchSize);
        const parts = await Promise.all(
          batch.map(async (uid) => {
            try {
              const q = `user_id=${uid}&start_date=${start}&end_date=${end}`;
              return await fetchAllFilteredTimesheets(q, {timeoutMs: 60000});
            } catch {
              return [];
            }
          })
        );
        parts.forEach((rows) => extra.push(...rows));
      }
      return [...timesheets, ...extra];
    },
    rebuildEmpReport() {
      this.empReportRows = buildPayrollEmpReportRows(
        this.selectedMonthKey,
        this.allItems,
        this.teamRows,
        this.rosterLookup,
        {
          apiTeamRows: this.apiTeamRows,
          rosterMetaByUsername: this.rosterMetaByUsername,
        }
      );
    },
    async loadPayrollData() {
      const mk = this.selectedMonthKey;
      if (!mk) return;
      const prevEmployee = this.selectedEmployeeUserId;
      this.loading = true;
      this.loadError = "";
      this.loadWarning = "";
      try {
        const start = GetStartOfMonthDate(`${mk}-01`);
        const end = GetEndOfMonthDate(`${mk}-01`);
        const filterQuery = `start_date=${start}&end_date=${end}`;
        const [teamRows, projects] = await Promise.all([
          this.fetchTeamRoster(),
          this.fetchProjects(),
        ]);
        this.apiTeamRows = teamRows;
        this.rosterMetaByUsername = buildKonekoTeamRosterMetaByUsername(teamRows);
        this.teamRows = teamRows;
        this.rosterLookup = buildTimesheetRosterLookup(teamRows);
        const projectNameById = {};
        (projects || []).forEach((p) => {
          if (p && p.id != null) {
            projectNameById[p.id] =
              p.project_name || p.name || `Project ${p.id}`;
          }
        });
        this.projectNameById = projectNameById;

        let timesheets = await fetchAllFilteredTimesheets(filterQuery, {
          timeoutMs: 300000,
        });
        const beforeBackfill = timesheets.length;
        timesheets = await this.backfillMissingUsers(timesheets, teamRows);
        if (timesheets.length > beforeBackfill) {
          this.loadWarning =
            "Some staff were loaded with per-person requests (org-wide list was incomplete).";
        }

        this.allItems = dedupeTimesheetRows(
          this.normalizeTimesheetRows(timesheets)
        ).sort((a, b) => {
          const da = String(a.log_date || "");
          const db = String(b.log_date || "");
          return da.localeCompare(db);
        });
        this.teamRows = enrichTeamRowsFromTimesheetUsers(
          this.teamRows,
          this.allItems,
          this.rosterLookup
        );
        this.rosterLookup = buildTimesheetRosterLookup(this.teamRows);
        this.rebuildEmpReport();
        this.payrollFlagsByUserId = buildPayrollFlagsByUserId(
          this.teamRows,
          this.empReportRows
        );

        const stillValid =
          prevEmployee != null &&
          (this.empReportRows || []).some((r) => r.userId === prevEmployee);
        if (stillValid) {
          this.selectedEmployeeUserId = prevEmployee;
        } else {
          this.pickDefaultEmployee();
        }
      } catch (e) {
        this.loadError =
          (e && e.message) || "Failed to load payroll timesheet data.";
        this.allItems = [];
        this.empReportRows = [];
        this.selectedEmployeeUserId = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.payroll-attendance-table-wrap {
  overflow-x: auto;
  max-width: 100%;
  padding-inline: 12px;
}
.payroll-attendance-table {
  min-width: 880px;
  font-size: 13px;
}
.payroll-attendance-table th {
  white-space: nowrap;
  background: #f5f5f5;
  vertical-align: bottom;
}
.payroll-bilingual-en {
  font-size: 0.85em;
  font-weight: normal;
  color: #666;
}
.payroll-day--weekend td {
  background: #fafafa;
  color: #757575;
}
.payroll-day--holiday td {
  background: #fff8e1;
}
.payroll-day--worked td:nth-child(7) {
  background: #e8f5e9;
}
.payroll-summary-table {
  max-width: 420px;
}
.payroll-summary-label {
  width: 220px;
  white-space: nowrap;
}
.payroll-summary-value {
  min-width: 80px;
}
.payroll-attendance-summary {
  padding-bottom: 32px;
}
</style>
